import {
  AccessTime as AccessTimeIcon,
  LocalPhoneOutlined as LocalPhoneIcon,
  TimerOutlined as TimerIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment/moment.js";
import { Link } from "react-router-dom";
import type { MxCallFragment } from "./MxCallListItem.generated.js";
import { getMxCallAvatarColor, getMxCallIcon } from "./MxCallTypeIcon.js";
import { useMxCallStatusTitle } from "./useMxCallStatusTitle.js";

// Formats a non-negative duration in seconds as MM:SS, adding an hours
// component (H:MM:SS) for calls of an hour or longer. Avoids moment's
// "mm:ss" wrapping at 59 minutes.
function formatCallDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

const META_ICON_SX = { fontSize: 14, color: "text.secondary" } as const;

// One icon + text pair in the meta row.
function MetaItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", color: "text.secondary" }}
    >
      {icon}
      <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {children}
      </Typography>
    </Stack>
  );
}

export function MxCallListItem({ call }: { call: MxCallFragment }) {
  const { t } = useTranslate(["Calls", "Global"]);
  const displayName =
    call.crmCompany?.contactPerson?.fullname ||
    call.crmCompany?.title ||
    call.incomingNumber ||
    call.outgoingNumber;
  // Always show the caller's number in the meta row.
  const phoneNumber = call.incomingNumber || call.outgoingNumber || null;

  const durationSeconds = Math.max(
    0,
    moment(call.endTime).diff(moment(call.startTime), "seconds")
  );
  const formattedDuration = formatCallDuration(durationSeconds);
  // Localized tokens, not a fixed German pattern — moment is configured from the
  // Tolgee language and the rest of the UI uses `L`/`LT` (e.g. PlanningSchedule).
  const formattedTime = moment(call.startTime).format("L LT");

  const statusTitle = useMxCallStatusTitle(call);
  const { Icon: CallIcon } = getMxCallIcon(call);
  const avatarColor = getMxCallAvatarColor(call.type);

  // A missed/voicemail call can be signalled via either `status` or `type`.
  // Voicemail takes precedence so a call never shows both chips.
  const isVoicemail = call.status === "voicemail" || call.type === "voicemail";
  const isMissed =
    !isVoicemail &&
    (call.status === "missed" ||
      call.type === "missed" ||
      call.type === "missed_outgoing");

  // Show the call source only when it is not the default `mxcall`, to avoid
  // cluttering the common case.
  const sourceLabelByKey: Record<string, string> = {
    mobile: t("Mobile", { ns: "Calls" }),
    matrix: t("Matrix", { ns: "Calls" }),
    other: t("Other", { ns: "Calls" }),
  };
  const sourceLabel =
    call.source && call.source !== "mxcall"
      ? (sourceLabelByKey[call.source] ?? call.source)
      : null;

  // Tooltips are hover-only, so this same label is also the accessible name of the
  // status dot (see `role="img"` below) — keyboard and screen-reader users would
  // otherwise never get the processed state.
  const processedLabel = call.isProcessed
    ? t("Processed", { ns: "Calls" })
    : t("Not processed", { ns: "Calls" });

  const hasChips =
    isMissed || isVoicemail || !!call.category?.name || !!sourceLabel;

  return (
    <ListItemButton
      component={Link}
      to={`/callassistant/call/${call.id}`}
      sx={{ alignItems: "center", gap: 1.5 }}
    >
      <Tooltip title={processedLabel}>
        <Box
          role="img"
          aria-label={processedLabel}
          sx={{
            width: 12,
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: call.isProcessed ? "grey.400" : "orange.main",
            }}
          />
        </Box>
      </Tooltip>

      <Tooltip title={statusTitle}>
        <Avatar
          role="img"
          aria-label={statusTitle}
          sx={{
            width: 36,
            height: 36,
            bgcolor: avatarColor,
            flexShrink: 0,
          }}
        >
          <CallIcon fontSize="small" sx={{ color: "common.white" }} />
        </Avatar>
      </Tooltip>

      <ListItemText
        primary={displayName}
        slotProps={{
          primary: { noWrap: true, sx: { fontWeight: 600 } },
          secondary: { component: "div" },
        }}
        secondary={
          <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              sx={{ alignItems: "center", flexWrap: "wrap" }}
            >
              {phoneNumber && (
                <MetaItem icon={<LocalPhoneIcon sx={META_ICON_SX} />}>
                  {phoneNumber}
                </MetaItem>
              )}
              <MetaItem icon={<TimerIcon sx={META_ICON_SX} />}>
                {formattedDuration}
              </MetaItem>
              <MetaItem icon={<AccessTimeIcon sx={META_ICON_SX} />}>
                {formattedTime}
              </MetaItem>
            </Stack>

            {hasChips && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                {isMissed && (
                  <Chip
                    label={t("Missed", { ns: "Calls" })}
                    size="small"
                    sx={{
                      bgcolor: "#fff2f0",
                      color: "#c62828",
                      border: "1px solid #ffa39e",
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                )}
                {isVoicemail && (
                  <Chip
                    label={t("Voicemail", { ns: "Calls" })}
                    size="small"
                    sx={{
                      bgcolor: "#fff7e6",
                      color: "#8a5a00",
                      border: "1px solid #ffd591",
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                )}
                {call.category?.name && (
                  <Chip
                    label={call.category.name}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                )}
                {sourceLabel && (
                  <Chip
                    label={sourceLabel}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        }
      />
    </ListItemButton>
  );
}
